import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";
import { type ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";
import { filenameFromPreview, hasFileSuffix } from "./inlineViewerPreview";
import { useInlineViewerUrl } from "./useInlineViewerUrl";

const HIC_SUFFIXES = [".hic"] as const;

export function isHicContactMapArtifact(preview: ArtifactPreview): boolean {
  return hasFileSuffix(preview, HIC_SUFFIXES);
}

interface HicViewerProps {
  preview: ArtifactPreview;
  className?: string;
}

// Juicebox range-reads the .hic straight from the presigned URL — only the
// footer index and the matrix blocks in view are fetched — so there is no
// whole-file download and no size cap here.
const HicViewer = ({ preview, className }: HicViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const filename = filenameFromPreview(preview);
  const url = useInlineViewerUrl(preview);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let registry: { deleteAll(): void } | null = null;

    const loadViewer = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!url) {
          throw new Error("No content URL available for this artifact.");
        }

        const { initHicRegistry } = await import("./juiceboxViewer");
        if (cancelled) return;

        const created = await initHicRegistry(container, {
          url,
          name: filename,
          queryParametersSupported: false,
        });
        if (cancelled) {
          created.deleteAll();
          return;
        }
        registry = created;
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load Hi-C contact map:", err);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadViewer();

    return () => {
      cancelled = true;
      registry?.deleteAll();
    };
  }, [url, filename]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className ?? "min-h-[640px]")}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-muted-foreground animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading contact map...</p>
          </div>
        </div>
      )}
      <div ref={containerRef} className={cn("w-full rounded-md", isLoading && "invisible")} />
    </div>
  );
};

export const HicContactMapArtifact = ({ preview }: { preview: ArtifactPreview }) => (
  <ArtifactShell
    preview={preview}
    expandedContent={<HicViewer preview={preview} className="h-full" />}
  >
    <FileArtifactBody preview={preview} />
    <div className="mt-3">
      <HicViewer preview={preview} />
    </div>
  </ArtifactShell>
);
